import {
  AvailableBlockOperatory,
  SearchingService,
} from 'interfaces/timeslotType';
import {
  findBestOperatory,
  findPractitionerWithBestOp,
} from 'utils/findOperatory';
import { IPractitionerOptions } from '../SingleBooking/SingleBooking';
import {
  getDentistIdForSplitScheduling,
  getOperatoryId,
} from 'pages/UpdatedSearchResultPage/utils';
import getChairOptimizationForExamCleaning from 'utils/getChairOptimizationForExamCleaning';

export const findSuitablePractitionerForNormalService = ({
  appointmentDate,
  timeSlot,
  serviceDuration,
  availableDoctorsFromSearchAPI,
}: {
  appointmentDate: string;
  timeSlot: number;
  serviceDuration: number;
  availableDoctorsFromSearchAPI: {
    id: string;
    title: string;
    doctorInsurances: { insuranceCarrierId: string; isInNetwork: boolean }[];
    availableBlocks: {
      date: string;
      availableBlockOperatories?: AvailableBlockOperatory[];
      blocks: number[];
      searchingService: SearchingService;
      isOptimizedApplied: boolean;
    }[];
  }[];
}) => {
  // LOOK FOR PRACTITIONER HAS SLOT ON SELECTED DATE
  const availablePractitioners = availableDoctorsFromSearchAPI.map((doctor) => {
    const operatoryForAppointmentDate = doctor.availableBlocks.find(
      (availableBock) =>
        availableBock.date === appointmentDate &&
        availableBock.blocks.includes(timeSlot)
    );
    return {
      ...doctor,
      availableBlocks: operatoryForAppointmentDate,
    };
  });

  let bestAvailableOperatoryForEachPractitioner = [];

  for (let i = 0; i < availablePractitioners.length; i++) {
    const practitioner = availablePractitioners[i];
    const availableBlockOperatories =
      practitioner.availableBlocks?.availableBlockOperatories;

    if (!availableBlockOperatories) {
      continue;
    }

    const bestOperatory = findBestOperatory({
      selectedSlot: timeSlot,
      availableBlockOperatories,
      serviceDuration: serviceDuration,
    });

    bestAvailableOperatoryForEachPractitioner.push({
      ...practitioner,
      bestOperatory,
    });
  }

  const bestPractitioner = findPractitionerWithBestOp<any>({
    selectedSlot: timeSlot,
    listOfPractitioners: bestAvailableOperatoryForEachPractitioner,
    serviceDuration: serviceDuration,
  });

  if (!bestPractitioner) {
    return null;
  }

  return {
    practitioner: {
      id: bestPractitioner.id,
      name: bestPractitioner.name,
      title: bestPractitioner.title,
      specialist: bestPractitioner.specialist,
    },
    operatoryId: bestPractitioner.bestOperatory.operatoryId,
  };
};

export const findSuitablePractitionerForExamAndCleaningService = ({
  appointmentDate,
  timeSlot,
  allDoctorOptions,
  doctorIdsWithMatchingSelectedTimeSlot,
  selectedHygienistId,
  dentist,
  availableDoctorsFromSearchAPI,
}: {
  appointmentDate: string;
  timeSlot: number;
  allDoctorOptions: IPractitionerOptions[];
  doctorIdsWithMatchingSelectedTimeSlot: string[];
  selectedHygienistId: string | null;
  dentist: {
    id: string;
    title: string;
  };
  availableDoctorsFromSearchAPI: {
    id: string;
    title: string;
    doctorInsurances: { insuranceCarrierId: string; isInNetwork: boolean }[];
    availableBlocks: {
      date: string;
      availableBlockOperatories?: AvailableBlockOperatory[];
      blocks: number[];
      searchingService: SearchingService;
      isOptimizedApplied: boolean;
    }[];
  }[];
}) => {
  let practitioner = allDoctorOptions.find(
    (practitioner) =>
      practitioner.id ===
      (selectedHygienistId || doctorIdsWithMatchingSelectedTimeSlot[0])
  );

  if (!practitioner) {
    return null;
  }

  const operatoryId = getOperatoryId({
    date: appointmentDate,
    doctorId: practitioner.id,
    timeSlot,
    doctors: availableDoctorsFromSearchAPI,
  });

  const dentistInfo = getDentistIdForSplitScheduling({
    date: appointmentDate,
    doctorId: selectedHygienistId,
    selectedDentistId: dentist.id === 'all' ? null : dentist.id,
    timeSlot,
    doctors: availableDoctorsFromSearchAPI,
  });

  let chairOptimizations = availableDoctorsFromSearchAPI.map((doctor) => {
    const availableBlock = doctor.availableBlocks.find(
      (availableBlock) => availableBlock.date === appointmentDate
    ) || {
      date: '',
      blocks: [],
      isOptimizedApplied: false,
      availableBlockOperatories: [],
    };

    const isOptimizedApplied = availableBlock.isOptimizedApplied;

    const chairOptimizationForExamCleaning =
      getChairOptimizationForExamCleaning({
        id: doctor.id,
        availableBlock,
        selectedSlot: timeSlot,
      });

    return { ...chairOptimizationForExamCleaning, isOptimizedApplied };
  });

  const isFoundPractitionerInChairOptimization = chairOptimizations.find(
    (chairOptimization) =>
      chairOptimization.doctorId === practitioner?.id &&
      chairOptimization.isOptimizedApplied
  );

  if (isFoundPractitionerInChairOptimization) {
    chairOptimizations = chairOptimizations.filter(
      (chairOptimization) => chairOptimization.doctorId === practitioner?.id
    );
  }

  return {
    practitioner: {
      id: practitioner.id,
      title: practitioner.title,
    },
    dentistInfo,
    chairOptimizations,
    operatoryId,
  };
};
